import { useTranslation } from 'react-i18next';

import { notifications } from '@mantine/notifications';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { API_URL } from '@/lib/apiUrl';
import { queryKeys } from '@/lib/queryKeys';
import {
  CriticalPathEntry,
  PlanningCache,
  applyIntervalUpdate,
  applyLockedTaskDateRollup,
  findTaskIdForInterval,
} from '@/modules/Planning/hooks/usePlanningProjects/planningCacheUtils';
import { showCascadeToast } from '@/modules/Planning/utils/showCascadeToast';
import { Interval, Task } from '@/types/types';

interface UseUpdateIntervalOptions {
  /**
   * Enable optimistic updates for planning projects cache
   * @default false
   */
  optimisticPlanningUpdate?: boolean;
}

const useUpdateInterval = (_options: UseUpdateIntervalOptions = {}) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('gantt');

  return useMutation({
    mutationFn: async ({
      interval,
      projectId,
      intervalId,
    }: {
      interval: Partial<Interval>;
      projectId: string | number | null;
      intervalId: string | number;
    }) => {
      const { data } = await axiosInstance.put<
        Interval & {
          cascaded_tasks?: Task[];
          task?: Task;
          critical_path?: Record<string, CriticalPathEntry>;
        }
      >(`${API_URL}/api/v1/intervals/${intervalId}?project_id=${projectId}`, interval);
      return data;
    },

    onMutate: async ({ intervalId, interval }) => {
      const queryKey = queryKeys.planningProjects._def;
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueriesData<PlanningCache>({ queryKey });

      queryClient.setQueriesData<PlanningCache>({ queryKey }, (old) => {
        if (!old) return old;
        const taskId = findTaskIdForInterval(old, intervalId);
        const updated = applyIntervalUpdate(old, intervalId, interval);
        return applyLockedTaskDateRollup(updated, taskId);
      });

      return { snapshot };
    },

    onError: (_err, _vars, context) => {
      context?.snapshot?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      notifications.show({
        title: 'Fejl',
        message: 'Kunne ikke opdatere interval',
        color: 'red',
      });
    },

    // No cache merge here — PlanningChannel's broadcast (usePlanningChannels,
    // applyPlanningBroadcast) is the confirmed-state path now, including for
    // our own writes (the server echoes this same write back over the
    // socket). onMutate's optimistic guess above is the only client-side
    // write; this just needs the response for the side effects below.
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.intervals.byAccount._def,
      });
      showCascadeToast(data.cascaded_tasks, t);
    },
  });
};

export default useUpdateInterval;
